// What every tool form of its own shares (G7-tool-forms): its route, its models, live's "Video"
// slot, live's unpriced words, the usage notes, the price and the start, and the errors under the
// panel.
import { ActionIcon, Group } from "@mantine/core";
import { ArrowUp, Film } from "lucide-react";
import type { ReactNode } from "react";

import type { Catalog, CatalogVideoModel, GenerationType, Job } from "../../../api/generated/model";
import { MediaMime } from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import type { PickedActor } from "../../../platform/actors/picked";
import type { PricedStart } from "../../../platform/jobs/priced-start";
import { UploadState, type ImageUpload } from "../../../platform/media/image-upload";
import { Price } from "../../../ui/Price";
import { UsageNotes } from "../../../ui/UsageNotes";
import { MediaRefsSlot } from "../video/MediaRefsSlot";
import type { ToolVideo } from "./use-tool-video";

// The brand's route, which the Studio's page and the Pro Studio redesign's Create pages both sit
// under: the composer reads its team and brand from it wherever it is drawn.
export const ROUTE = "/signed-in/teams/$teamId/brands/$brandId";
export const VIDEO_TYPES = MediaMime.options.filter((mime) => mime.startsWith("video/"));
/** Live's words on the bolt while a tool's form has no price (`uft-estimate/constants.ts`). */
export const UNPRICED = "We couldn’t calculate the estimated generation cost right now.";

export interface ToolFormProps {
  catalog: Catalog;
  /** The Actions toolbar above the panel, and the Actions panel over it while open. */
  above: ReactNode;
  overlay: ReactNode;
  onStarted: (job: Job) => void;
  /** The actor AI Actors' "Create a Talking Head" brought, for the Talking Head; none else. */
  actor?: PickedActor | null;
  /** A job of this tool's to start again (`?clone=`): its model, prompt and files, where the form
   *  takes them back (Motion Control; issue 828, item 23). */
  clone?: Job | undefined;
  /** In a card of its own, not floating (`GlassPanel`'s `inline`, the Pro Studio redesign). */
  inline?: boolean | undefined;
}

/** A tool's models: the catalog's rows of its job type, in the catalog's order. */
export function rowsOf(catalog: Catalog, type: GenerationType): CatalogVideoModel[] {
  return catalog.video_models.filter((model) => model.job_types.includes(type));
}

/** The model a form starts on: the catalog's default, else the first (live's rule). */
export function defaultModel(models: CatalogVideoModel[]): CatalogVideoModel {
  return models.find((model) => model.is_default) ?? models[0]!;
}

/**
 * A video slot, From library or Upload: live's "Video", or Motion Control's "Video ref"; with the
 * model's source limits in its tooltip where it has them (the dub's).
 */
export function VideoSlot(props: { tool: ToolVideo; label?: string; limits?: string[] }) {
  const { tool, label = "Video", limits } = props;
  return (
    <MediaRefsSlot
      label={label}
      tag="Video"
      icon={<Film size={20} strokeWidth={1.5} aria-hidden />}
      accept={VIDEO_TYPES}
      refs={limits === undefined ? tool.slot : { ...tool.slot, limits }}
    />
  );
}

/**
 * Live's usage notes for the tool's job (#377, G6 decision 15, capture 13b), the price and the
 * start, grey until the form can start.
 */
export function StartGroup({ job, notes }: { job: PricedStart; notes: readonly string[] }) {
  return (
    <Group gap={12} wrap="nowrap">
      <UsageNotes notes={notes} />
      <Price priced={job.priced} pending={job.pricing} unpriced={UNPRICED} />
      <ActionIcon
        variant="filled"
        size="lg"
        radius="100%"
        aria-label="Start"
        disabled={!job.canStart}
        loading={job.start.isPending}
        onClick={job.startJob}
      >
        <ArrowUp size={20} />
      </ActionIcon>
    </Group>
  );
}

/** The uploads' states, then a refused quote or start, under the panel's body. */
export function Errors({ job, uploads }: { job: PricedStart; uploads: ImageUpload[] }) {
  return (
    <>
      {uploads.map((upload, at) => (
        <UploadState key={at} upload={upload} />
      ))}
      {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
      {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
    </>
  );
}
